import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportRowsToCsv } from '../utils/comparisonUtils';

const SERIES_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#db2777',
];

const AnalyticsComparisonChart = memo(({ scenarios, height = 400 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedMetric, setSelectedMetric] = useState('');
  const [selectedSecurity, setSelectedSecurity] = useState('');

  const scenarioEntries = useMemo(() => Object.entries(scenarios || {}), [scenarios]);

  const metrics = useMemo(() => {
    const result = [];
    const seen = new Set();
    scenarioEntries.forEach(([, scenario]) => {
      if (!Array.isArray(scenario?.analytics)) return;
      scenario.analytics.forEach((row) => {
        const metric = row?.Metric;
        if (!metric || seen.has(metric)) return;
        seen.add(metric);
        result.push(metric);
      });
    });
    return result;
  }, [scenarioEntries]);

  const securities = useMemo(() => {
    const result = [];
    const seen = new Set();
    scenarioEntries.forEach(([, scenario]) => {
      if (!Array.isArray(scenario?.analytics)) return;
      scenario.analytics.forEach((row) => Object.keys(row || {}).forEach((key) => {
        if (key === 'Metric' || !/^Tranche\s+.+:/i.test(key) || seen.has(key)) return;
        seen.add(key);
        result.push(key);
      }));
    });
    return result;
  }, [scenarioEntries]);

  useEffect(() => {
    if (!metrics.includes(selectedMetric)) {
      setSelectedMetric(metrics.includes('WAL') ? 'WAL' : (metrics[0] || ''));
    }
  }, [metrics, selectedMetric]);

  useEffect(() => {
    if (!securities.includes(selectedSecurity)) setSelectedSecurity(securities[0] || '');
  }, [securities, selectedSecurity]);

  const chartData = useMemo(() => scenarioEntries.map(([label, scenario], index) => {
    const metricRow = Array.isArray(scenario?.analytics)
      ? scenario.analytics.find(row => row?.Metric === selectedMetric)
      : null;
    const rawValue = metricRow?.[selectedSecurity];
    const numericValue = rawValue === null || rawValue === undefined || rawValue === ''
      ? null
      : Number(rawValue);

    return {
      scenario: label,
      value: Number.isFinite(numericValue) ? numericValue : null,
      color: SERIES_COLORS[index % SERIES_COLORS.length],
    };
  }), [scenarioEntries, selectedMetric, selectedSecurity]);

  const hasValues = chartData.some(point => point.value !== null);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
        <FormControl sx={{ minWidth: { xs: '100%', sm: 240 } }} size="small">
          <InputLabel id="analytics-metric-select-label">Metric</InputLabel>
          <Select
            labelId="analytics-metric-select-label"
            id="analytics-metric-select"
            value={selectedMetric}
            onChange={(event) => setSelectedMetric(event.target.value)}
            label="Metric"
          >
            {metrics.map(metric => <MenuItem key={metric} value={metric}>{metric}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: { xs: '100%', sm: 300 } }} size="small">
          <InputLabel id="analytics-security-select-label">Security</InputLabel>
          <Select
            labelId="analytics-security-select-label"
            id="analytics-security-select"
            value={selectedSecurity}
            onChange={(event) => setSelectedSecurity(event.target.value)}
            label="Security"
          >
            {securities.map(security => (
              <MenuItem key={security} value={security}>
                {security.replace(/^Tranche\s+/i, '')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          disabled={!hasValues}
          sx={{ alignSelf: 'center', ml: { sm: 'auto' } }}
          onClick={() => exportRowsToCsv(
            chartData.map(({ scenario, value }) => ({
              scenario,
              metric: selectedMetric,
              security: selectedSecurity,
              value,
            })),
            'analytics_chart'
          )}
        >
          Export CSV
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, minHeight: height }}>
        {hasValues ? (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? theme.palette.divider : '#e2e8f0'}
              />
              <XAxis
                dataKey="scenario"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => Number(value).toLocaleString(undefined, {
                  maximumFractionDigits: 4,
                })}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  color: theme.palette.text.primary,
                }}
                formatter={(value) => [Number(value).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                }), selectedMetric]}
              />
              <Bar dataKey="value" name={selectedMetric}>
                {chartData.map(point => <Cell key={point.scenario} fill={point.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height,
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="body1">
              No analytics available for the selected metric and security
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
});

AnalyticsComparisonChart.displayName = 'AnalyticsComparisonChart';

export default AnalyticsComparisonChart;
